import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/api/client";
import { Rocket, CheckCircle2, Clock, AlertCircle, ArrowRight, Pause, SkipForward, Filter, RefreshCw } from "lucide-react";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_META = {
  pending: { label: "Bekliyor", icon: Clock, color: "text-[#94A3B8]", bg: "bg-[#F4F4F5]" },
  in_progress: { label: "Devam ediyor", icon: ArrowRight, color: "text-[#4F46E5]", bg: "bg-[#EEF2FF]" },
  waiting_customer: { label: "Müşteriden bekleniyor", icon: Pause, color: "text-[#F59E0B]", bg: "bg-[#FFFBEB]" },
  done: { label: "Tamamlandı", icon: CheckCircle2, color: "text-[#10B981]", bg: "bg-[#ECFDF5]" },
  skipped: { label: "Atlandı", icon: SkipForward, color: "text-[#64748B]", bg: "bg-[#FAFAFB]" },
};

export default function OnboardingPage() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filter, setFilter] = useState("active"); // active | completed | all
  const [backfilling, setBackfilling] = useState(false);
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = () => Promise.all([
    apiClient.get("/onboardings").then(r=>r.data),
    apiClient.get("/customers").then(r=>r.data),
  ]).then(([o, c]) => { setItems(o); setCustomers(c); });
  useEffect(() => { load(); }, []);

  const backfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await apiClient.post("/onboardings/backfill");
      toast.success(`${data.created} müşteri için onboarding oluşturuldu`);
      load();
    } catch (e) { toast.error("İşlem başarısız"); }
    finally { setBackfilling(false); }
  };

  const cust = (cid) => customers.find(c => c.id === cid);
  // Skip orphans (onboarding records whose customer was deleted) — defensive guard
  const filtered = items.filter(o => cust(o.customer_id)).filter(o => filter==="all" || (filter==="completed" ? !!o.completed_at : !o.completed_at));

  return (
    <div className="space-y-5" data-testid="onboarding-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Rocket size={22} className="text-[#4F46E5]"/>Müşteri Onboarding</h1>
          <p className="text-sm text-[#64748B] mt-1">Yeni müşteri kayıtları için adım adım süreç takibi</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <button onClick={backfill} disabled={backfilling} className="ky-btn ky-btn-outline ky-btn-sm" data-testid="onboarding-backfill"><RefreshCw size={11} className={backfilling?"animate-spin":""}/>Mevcut müşteriler için oluştur</button>}
          <div className="flex items-center gap-1 ky-card p-1">
            {[["active","Aktif"],["completed","Tamamlandı"],["all","Tümü"]].map(([k,l]) => (
              <button key={k} onClick={()=>setFilter(k)} className={`px-3 py-1 text-xs rounded-md ${filter===k?"bg-[#4F46E5] text-white":"text-[#64748B]"}`} data-testid={`onboarding-filter-${k}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      {filtered.length === 0 && <div className="ky-card p-12 text-center text-[#94A3B8]"><Rocket size={32} className="mx-auto mb-3 opacity-40"/>{filter==="active" ? "Aktif onboarding yok." : filter==="completed" ? "Henüz tamamlanmış onboarding yok." : "Henüz müşteri onboardingi yok."}</div>}

      <div className="grid lg:grid-cols-2 gap-4">
        {filtered.map(ob => {
          const c = cust(ob.customer_id);
          return (
            <div key={ob.id} className="ky-card p-5 ky-card-hover cursor-pointer" onClick={()=>nav(`/musteriler/${ob.customer_id}?tab=onboarding`)} data-testid={`onboarding-card-${ob.customer_id}`}>
              <div className="flex items-start gap-3 mb-3">
                <Avatar name={c?.name || "?"} size={40}/>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold">{c?.name || "?"}</div>
                  <div className="text-xs text-[#64748B]">{c?.company || "—"}</div>
                </div>
                {ob.completed_at && <span className="ky-badge ky-badge-success"><CheckCircle2 size={9}/>Tamamlandı</span>}
              </div>
              <div className="mb-2 flex items-center justify-between text-xs">
                <span className="text-[#64748B]">{ob.done_count}/{ob.total_count} adım</span>
                <span className="font-mono font-semibold">%{ob.progress}</span>
              </div>
              <div className="h-1.5 bg-[#F4F4F5] rounded-full overflow-hidden mb-3"><div className={`h-full transition-all ${ob.progress === 100 ? "bg-[#10B981]" : "bg-gradient-to-r from-[#4F46E5] to-[#7C3AED]"}`} style={{width: `${ob.progress}%`}}/></div>
              {ob.resume_step && (
                <div className="text-xs text-[#4F46E5] flex items-center gap-1"><ArrowRight size={11}/>Sıradaki: <strong>{ob.steps.find(s=>s.key===ob.resume_step)?.label}</strong></div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Detail view component (used inside customer detail page tab)
export function OnboardingDetail({ customerId }) {
  const [ob, setOb] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = () => apiClient.get(`/onboarding/${customerId}`).then(r=>setOb(r.data));
  useEffect(() => { load(); }, [customerId]);

  const update = async (step_key, status, notes="") => {
    setSaving(step_key);
    try { await apiClient.put(`/onboarding/${customerId}/${step_key}`, { status, notes }); load(); }
    finally { setSaving(null); }
  };

  if (!ob) return <div className="text-sm text-[#94A3B8]">Yükleniyor…</div>;

  return (
    <div className="space-y-4" data-testid="onboarding-detail">
      <div className="ky-card p-4 bg-gradient-to-br from-[#EEF2FF] to-white">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#4F46E5] font-semibold">Onboarding Süreci</div>
            <div className="text-lg font-bold mt-0.5">{ob.done_count}/{ob.total_count} adım tamamlandı</div>
          </div>
          <div className="text-3xl font-bold tabular-nums">%{ob.progress}</div>
        </div>
        <div className="h-2 bg-white rounded-full overflow-hidden mt-3"><div className={`h-full ${ob.progress === 100 ? "bg-[#10B981]" : "bg-gradient-to-r from-[#4F46E5] to-[#7C3AED]"}`} style={{width: `${ob.progress}%`}}/></div>
        {ob.resume_step && <div className="text-xs text-[#4F46E5] mt-3 flex items-center gap-1"><ArrowRight size={11}/>Devam et: <strong>{ob.steps.find(s=>s.key===ob.resume_step)?.label}</strong></div>}
      </div>

      <div className="space-y-2">
        {ob.steps.map((s, i) => {
          const meta = STATUS_META[s.status];
          return (
            <div key={s.key} className={`ky-card p-3 flex items-center gap-3 ${meta.bg}`} data-testid={`onboarding-step-${s.key}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${meta.color} bg-white shrink-0`}>
                <meta.icon size={14}/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium">{i+1}. {s.label}</span>
                  {s.is_auto && <span className="ky-badge ky-badge-neutral">Otomatik</span>}
                  {s.wait_customer && s.status !== "done" && <span className="ky-badge ky-badge-warning">Müşteri bekleniyor</span>}
                </div>
                <div className={`text-[11px] mt-0.5 ${meta.color}`}>{meta.label}</div>
                {s.completed_at && <div className="text-[10px] text-[#94A3B8] font-mono mt-0.5">{new Date(s.completed_at).toLocaleString("tr-TR")}</div>}
              </div>
              {s.status !== "done" ? (
                <div className="flex gap-1">
                  {s.wait_customer && s.status !== "waiting_customer" && <button onClick={()=>update(s.key, "waiting_customer")} disabled={saving===s.key} className="ky-btn ky-btn-outline ky-btn-sm" data-testid={`step-wait-${s.key}`}><Pause size={11}/>Bekle</button>}
                  <button onClick={()=>update(s.key, "done")} disabled={saving===s.key} className="ky-btn ky-btn-accent ky-btn-sm" data-testid={`step-done-${s.key}`}><CheckCircle2 size={11}/>Tamamla</button>
                  <button onClick={()=>update(s.key, "skipped")} disabled={saving===s.key} className="ky-btn ky-btn-ghost ky-btn-sm" data-testid={`step-skip-${s.key}`}><SkipForward size={11}/></button>
                </div>
              ) : (
                <button onClick={()=>update(s.key, "pending")} className="ky-btn ky-btn-ghost ky-btn-sm" data-testid={`step-reopen-${s.key}`}>Geri al</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
